import 'styles/pages/features/reports.scss';
import '~scripts/main';

// Packages
import Vue from 'vue';
import eases from 'eases';
import { createPopper } from '@popperjs/core';

// Swiper
import Swiper from 'swiper';
import { Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([SwiperPagination]);

// Core
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';
import { isInViewport } from '~scripts/core/helpers';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';
import initSubscribeForm from '~scripts/blocks/subscribe-form';
import initRequestForm from '~scripts/blocks/request-form';
import ClientsLogotypes from '~scripts/blocks/vue/ClientsLogotypes.vue';

// Components
import '~scripts/components/modals';

// Breakpoints
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// POPOVERS
//
const popoversModifiers = [
  {
    name: 'preventOverflow',
    enabled: false,
  },
  {
    name: 'flip',
    enabled: false,
  },
];

//
// ANIMATIONS
//
// ANALYTICS CENTER
let stopAnalyticsCenterAnimation = null;

const handleAnalyticsCenterAnimation = () => {
  const canvas = document.getElementById('analyticsCenterCanvas');

  if (mediaSm.matches) {
    if (stopAnalyticsCenterAnimation && typeof stopAnalyticsCenterAnimation === 'function') {
      stopAnalyticsCenterAnimation();
    }
  } else {
    stopAnalyticsCenterAnimation = scrollAnimationProgress(canvas, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        const height = el.offsetHeight;
        return rect.top + window.scrollY - (window.innerHeight - height / 4);
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + height / 1.5;
      },
    });
  }
};

handleAnalyticsCenterAnimation();
mediaSm.addEventListener('change', handleAnalyticsCenterAnimation);

// KEY METRICS
let stopKeyMetricAnimation = [];

const handleKeyMetricAnimation = () => {
  const keyMetricBlocks = document.querySelectorAll('.js-key-metric-block');

  if (mediaSm.matches) {
    stopKeyMetricAnimation.forEach((stop) => stop());
  } else {
    stopKeyMetricAnimation = Array.from(keyMetricBlocks, (keyMetricBlock) => {
      return scrollAnimationProgress(keyMetricBlock, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
      });
    });
  }
};

handleKeyMetricAnimation();
mediaSm.addEventListener('change', handleKeyMetricAnimation);

// FEATURES
// List
const featuresListItems = document.querySelectorAll('#featuresList > li');

featuresListItems.forEach((listItem) => {
  scrollAnimationProgress(
    listItem,
    {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        const height = el.offsetHeight;
        return rect.top + window.scrollY - (window.innerHeight + 80 - height / 2);
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + height * 4;
      },
    },
    eases.cubicOut
  );
});

// Items
let stopFeaturesItemsAnimation = [];

const handleFeaturesItemsAnimation = () => {
  const featuresItems = document.querySelectorAll('.js-features-items');

  if (mediaSm.matches) {
    stopFeaturesItemsAnimation.forEach((stop) => stop());
  } else {
    stopFeaturesItemsAnimation = Array.from(featuresItems, (featureItem) => {
      return scrollAnimationProgress(featureItem, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
      });
    });
  }
};

handleFeaturesItemsAnimation();
mediaSm.addEventListener('change', handleFeaturesItemsAnimation);

// Blocks
let stopFeaturesBlocksAnimation = [];

const handleFeaturesBlocksAnimation = () => {
  const featuresBlocks = document.querySelectorAll('.js-features-blocks');

  if (mediaSm.matches) {
    stopFeaturesBlocksAnimation.forEach((stop) => stop());
  } else {
    stopFeaturesBlocksAnimation = Array.from(featuresBlocks, (featureBlock) => {
      return scrollAnimationProgress(featureBlock, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
      });
    });
  }
};

handleFeaturesBlocksAnimation();
mediaSm.addEventListener('change', handleFeaturesBlocksAnimation);

// FUNNEL PASSAGE
document.addEventListener('scroll', () => {
  const windowHeight = window.innerHeight;
  const header = document.getElementById('header');
  const headerHeight = header.offsetHeight;
  const canvas = document.getElementById('funnelPassageCanvas');
  const offset = (windowHeight - headerHeight) * 0.7;

  if (isInViewport(canvas, true, offset)) {
    canvas.dataset.popover = 'visible';
  }
});

// Popover
const funnelPassageRefSm = document.querySelector('#funnelPassageRefSm');
const funnelPassageRefMd = document.querySelector('#funnelPassageRefMd');
const funnelPassagePop = document.querySelector('#funnelPassagePop');

const handleFunnelPassagePopover = () => {
  if (mediaSm.matches) {
    const modifiers = [
      {
        name: 'offset',
        options: {
          offset: [20, -20],
        },
      },
    ].concat(popoversModifiers);

    createPopper(funnelPassageRefSm, funnelPassagePop, {
      placement: 'top-end',
      modifiers: modifiers,
    });
  } else {
    const modifiers = [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ].concat(popoversModifiers);

    createPopper(funnelPassageRefMd, funnelPassagePop, {
      placement: 'top-end',
      modifiers: modifiers,
    });
  }
};

handleFunnelPassagePopover();
mediaSm.addEventListener('change', handleFunnelPassagePopover);

// FUNNEL KEY INDICATORS
const FUNNEL_KEY_INDICATORS_STEPS = 4;

// Video
const handleFunnelKeyIndicatorsVideoPlay = () => {
  const funnelKeyIndicatorsVideoWrap = document.getElementById('funnelKeyIndicatorsVideoWrap');

  funnelKeyIndicatorsVideoWrap.classList.remove('funnel-key-indicators__video--loading');
};

const handleFunnelKeyIndicatorsVideoToggle = () => {
  const funnelKeyIndicatorsVideo = document.getElementById('funnelKeyIndicatorsVideo');

  if (mediaSm.matches) {
    document.addEventListener('scroll', handleFunnelKeyIndicatorsVideoToggle);

    funnelKeyIndicatorsVideo.addEventListener('canplay', handleFunnelKeyIndicatorsVideoPlay);
    funnelKeyIndicatorsVideo.addEventListener('play', handleFunnelKeyIndicatorsVideoPlay);

    const offset = funnelKeyIndicatorsVideo.offsetHeight / 2;

    if (isInViewport(funnelKeyIndicatorsVideo, true, offset)) {
      funnelKeyIndicatorsVideo.play();
    } else {
      funnelKeyIndicatorsVideo.pause();
    }

    if (!isInViewport(funnelKeyIndicatorsVideo)) {
      funnelKeyIndicatorsVideo.currentTime = 0;
    }
  } else {
    document.removeEventListener('scroll', handleFunnelKeyIndicatorsVideoToggle);

    funnelKeyIndicatorsVideo.removeEventListener('canplay', handleFunnelKeyIndicatorsVideoPlay);
    funnelKeyIndicatorsVideo.removeEventListener('play', handleFunnelKeyIndicatorsVideoPlay);
    funnelKeyIndicatorsVideo.pause();
    funnelKeyIndicatorsVideo.currentTime = 0;
  }
};

handleFunnelKeyIndicatorsVideoToggle();
mediaSm.addEventListener('change', handleFunnelKeyIndicatorsVideoToggle);

// Popovers
for (let index = 0; index < FUNNEL_KEY_INDICATORS_STEPS; index++) {
  const indexNormalized = index + 1;

  createPopper(
    document.getElementById(`funnelKeyIndicatorsRef${indexNormalized}`),
    document.getElementById(`funnelKeyIndicatorsPop${indexNormalized}`),
    {
      placement: 'top',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
      ].concat(popoversModifiers),
    }
  );
}

// Scrolling
const handleFunnelKeyIndicatorsScrolling = () => {
  if (mediaSm.matches) {
    document.removeEventListener('scroll', handleFunnelKeyIndicatorsScrolling);
  } else {
    document.addEventListener('scroll', handleFunnelKeyIndicatorsScrolling);

    const windowHeight = window.innerHeight;
    const header = document.getElementById('header');
    const headerHeight = header.offsetHeight;
    const canvas = document.getElementById('funnelKeyIndicatorsCanvas');
    const canvasRect = canvas.getBoundingClientRect();
    const offset = (windowHeight - headerHeight) * 0.6;
    const canvasTop = canvasRect.top;

    if (isInViewport(canvas, true, offset)) {
      canvas.dataset.step = '1';

      if (canvasTop <= windowHeight - offset * 1.33) {
        canvas.dataset.step = '2';
      }
      if (canvasTop <= windowHeight - offset * 1.66) {
        canvas.dataset.step = '3';
      }
      if (canvasTop <= windowHeight - offset * 1.99) {
        canvas.dataset.step = '4';
      }
    } else {
      canvas.removeAttribute('data-step');
    }
  }
};

handleFunnelKeyIndicatorsScrolling();
mediaSm.addEventListener('change', handleFunnelKeyIndicatorsScrolling);

// PERSONAL EFFICIENCY
const personalEfficiency = document.getElementById('personalEfficiency');

scrollAnimationProgress(personalEfficiency, {
  getStart(el) {
    const rect = el.getBoundingClientRect();
    return rect.top + window.scrollY - (window.innerHeight - 200);
  },
  getEnd(el, start) {
    const height = el.offsetHeight;
    return start + window.innerHeight + height;
  },
});

// CANDIDATES MOVEMENT
// Slider
let candidatesMovementSlider = null;

const handleCandidatesMovementSlider = () => {
  if (mediaSm.matches) {
    candidatesMovementSlider = new Swiper('#candidatesMovementSlider', {
      allowTouchMove: true,
      centeredSlides: true,
      effect: 'slide',
      navigation: false,
      noSwiping: false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      preventClicks: true,
      preventClicksPropagation: true,
      slidesOffsetBefore: -30,
      slidesOffsetAfter: -30,
      slidesPerGroup: 1,
      slidesPerView: 1,
      spaceBetween: -48,
      width: 360,
    });
  } else {
    if (candidatesMovementSlider) {
      candidatesMovementSlider.destroy();
    }
  }
};

handleCandidatesMovementSlider();
mediaSm.addEventListener('change', handleCandidatesMovementSlider);

// Animation
const candidatesMovementCoordinates = [
  [0, 0],
  [60, 180],
  [130, 190],
];
let stopCandidatesMovementAnimation = [];

const handleCandidatesMovementAnimation = () => {
  const candidatesMovementCards = document.querySelectorAll('.js-candidates-movement-card');

  if (mediaSm.matches) {
    stopCandidatesMovementAnimation.forEach((stop) => stop());
  } else {
    stopCandidatesMovementAnimation = Array.from(candidatesMovementCards, (candidatesMovementCard, index) => {
      return scrollAnimationProgress(candidatesMovementCard, {
        getStart() {
          const rect = candidatesMovementCards[0].getBoundingClientRect();
          const height = candidatesMovementCards[0].offsetHeight;
          return rect.top + window.scrollY - (window.innerHeight - height - candidatesMovementCoordinates[index][0]);
        },
        getEnd(el, start) {
          return start + candidatesMovementCoordinates[index][1];
        },
        lerpValue: 0.25,
      });
    });
  }
};

handleCandidatesMovementAnimation();
mediaSm.addEventListener('change', handleCandidatesMovementAnimation);

// SATISFACTION LEVEL
let stopSatisfactionLevelAnimation = [];

const handleSatisfactionLevelAnimation = () => {
  const satisfactionLevelBlocks = document.querySelectorAll('.js-satisfaction-level-block');

  if (mediaSm.matches) {
    stopSatisfactionLevelAnimation.forEach((stop) => stop());
  } else {
    stopSatisfactionLevelAnimation = Array.from(satisfactionLevelBlocks, (satisfactionLevelBlock) => {
      return scrollAnimationProgress(satisfactionLevelBlock, {
        getStart(el) {
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - window.innerHeight;
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + height;
        },
      });
    });
  }
};

handleSatisfactionLevelAnimation();
mediaSm.addEventListener('change', handleSatisfactionLevelAnimation);

// DATA FILTERING
let stopDataFilteringAnimation = null;

const handleDataFilteringAnimation = () => {
  const dataFiltering = document.getElementById('dataFiltering');

  if (mediaSm.matches) {
    if (stopDataFilteringAnimation && typeof stopDataFilteringAnimation === 'function') {
      stopDataFilteringAnimation();
    }
  } else {
    stopDataFilteringAnimation = scrollAnimationProgress(dataFiltering, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + window.innerHeight + height;
      },
    });
  }
};

handleDataFilteringAnimation();
mediaSm.addEventListener('change', handleDataFilteringAnimation);

// EMAIL
// Slider
let emailSlider = null;

const handleEmailSlider = () => {
  if (mediaSm.matches) {
    emailSlider = new Swiper('#emailSlider', {
      allowTouchMove: true,
      centeredSlides: true,
      effect: 'slide',
      navigation: false,
      noSwiping: false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      preventClicks: true,
      preventClicksPropagation: true,
      slidesOffsetBefore: -30,
      slidesOffsetAfter: -30,
      slidesPerGroup: 1,
      slidesPerView: 1,
      spaceBetween: -48,
      width: 360,
    });
  } else {
    if (emailSlider) {
      emailSlider.destroy();
    }
  }
};

handleEmailSlider();
mediaSm.addEventListener('change', handleEmailSlider);

// Animation
let stopEmailAnimation = null;

const handleEmailAnimation = () => {
  const emailCanvas = document.getElementById('emailCanvas');

  if (mediaSm.matches) {
    if (stopEmailAnimation && typeof stopEmailAnimation === 'function') {
      stopEmailAnimation();
    }
  } else {
    stopEmailAnimation = scrollAnimationProgress(emailCanvas, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + height * 0.5;
      },
    });
  }
};

handleEmailAnimation();
mediaSm.addEventListener('change', handleEmailAnimation);

// INTERVIEW PROBLEMS
let stopInterviewProblemsAnimation = null;

const handleInterviewProblemsAnimation = () => {
  const interviewProblemsImage = document.getElementById('interviewProblemsImage');

  if (mediaSm.matches) {
    if (stopInterviewProblemsAnimation && typeof stopInterviewProblemsAnimation === 'function') {
      stopInterviewProblemsAnimation();
    }
  } else {
    stopInterviewProblemsAnimation = scrollAnimationProgress(interviewProblemsImage, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - window.innerHeight;
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + height * 0.5;
      },
    });
  }
};

handleInterviewProblemsAnimation();
mediaSm.addEventListener('change', handleInterviewProblemsAnimation);

//
// CLIENTS
//
new Vue({
  el: '#VClientsLogotypes',
  name: 'VClientsLogotypes',
  components: {
    ClientsLogotypes,
  },
});

//
// DOWNLOAD PDF FORM
//
const downloadPdfForm = document.getElementById('downloadPdfForm');

initSubscribeForm(downloadPdfForm);

//
// DEMO REQUEST FORM
//
const demoRequestForms = document.querySelectorAll('.js-demo-request-form');

demoRequestForms.forEach((form) => {
  initRequestForm(form);
});
